import type { ReactNode } from 'react';
import { useProjects, useReport, useSource } from '../../api/queries';
import type { EvidenceLink, EvidenceMetadata, EvidenceRelation, Finding, SourceVersion } from '../../api/types';
import { Field } from '../../components/Form';
import { SourcePicker } from '../../components/SourcePicker';
import { localToIso } from '../../lib/events';
import { asList, evidenceClassLabel, formatDate, relationMeta } from '../../lib/format';

export interface EvidenceDraft {
  key: number;
  sourceId: string;
  versionId: string;
  relation: EvidenceRelation;
  note: string;
  /** Classification metadata travels with the link untouched. */
  metadata?: EvidenceMetadata;
}

export interface FindingDraft {
  title: string;
  summary: string;
  interpretation: string;
  scope: string;
  method: string;
  /** `datetime-local` value, read as UTC. */
  sourceCutoff: string;
  uncertainty: string;
  implications: string;
  counterexamples: string;
  themes: string;
  chapter: string;
  projectId: string;
  evidence: EvidenceDraft[];
}

const splitLines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

const splitThemes = (text: string) =>
  text
    .split(',')
    .map((theme) => theme.trim().toLowerCase().replace(/\s+/g, '-'))
    .filter((theme) => theme !== '');

export function toLocalInput(iso: string): string {
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
}

export function draftFrom(finding: Finding): FindingDraft {
  return {
    title: finding.title,
    summary: finding.summary,
    interpretation: finding.interpretation,
    scope: finding.scope,
    method: finding.method,
    sourceCutoff: toLocalInput(finding.sourceCutoff),
    uncertainty: asList(finding.uncertainty).join('\n'),
    implications: asList(finding.implications).join('\n'),
    counterexamples: finding.counterexamples.join('\n'),
    themes: finding.themes.join(', '),
    chapter: finding.chapter,
    projectId: finding.projectId ?? '',
    evidence: finding.evidence.map((link, index) => ({
      key: index + 1,
      sourceId: link.sourceId,
      versionId: link.versionId ?? '',
      relation: link.relation,
      note: link.note ?? '',
      ...(link.metadata !== undefined ? { metadata: link.metadata } : {}),
    })),
  };
}

function normalise(link: EvidenceLink): EvidenceLink {
  return {
    sourceId: link.sourceId,
    ...(link.versionId !== undefined && link.versionId !== '' ? { versionId: link.versionId } : {}),
    relation: link.relation,
    ...(link.note !== undefined && link.note !== '' ? { note: link.note } : {}),
    ...(link.metadata !== undefined ? { metadata: link.metadata } : {}),
  };
}

export function evidenceFromDraft(rows: EvidenceDraft[]): EvidenceLink[] {
  return rows
    .filter((row) => row.sourceId !== '')
    .map((row) =>
      normalise({
        sourceId: row.sourceId,
        ...(row.versionId !== '' ? { versionId: row.versionId } : {}),
        relation: row.relation,
        ...(row.note.trim() !== '' ? { note: row.note.trim() } : {}),
        ...(row.metadata !== undefined ? { metadata: row.metadata } : {}),
      }),
    );
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Only fields the steward actually changed; the API keeps everything else from the current version. */
export function diffDraft(finding: Finding, draft: FindingDraft): Partial<Finding> {
  const changes: Partial<Finding> = {};
  for (const key of ['title', 'summary', 'interpretation', 'scope', 'method'] as const) {
    const value = draft[key].trim();
    if (value !== finding[key].trim()) changes[key] = value;
  }
  const uncertainty = splitLines(draft.uncertainty);
  if (!same(uncertainty, asList(finding.uncertainty))) changes.uncertainty = uncertainty;
  const implications = splitLines(draft.implications);
  if (!same(implications, asList(finding.implications))) changes.implications = implications;
  const counterexamples = splitLines(draft.counterexamples);
  if (!same(counterexamples, asList(finding.counterexamples))) changes.counterexamples = counterexamples;
  const themes = splitThemes(draft.themes);
  if (!same(themes, finding.themes)) changes.themes = themes;
  if (draft.chapter !== finding.chapter) changes.chapter = draft.chapter;
  if (draft.projectId !== '' && draft.projectId !== finding.projectId) changes.projectId = draft.projectId;
  if (draft.sourceCutoff !== toLocalInput(finding.sourceCutoff)) {
    const iso = localToIso(draft.sourceCutoff);
    if (iso !== null) changes.sourceCutoff = iso;
  }
  const evidence = evidenceFromDraft(draft.evidence);
  if (!same(evidence, finding.evidence.map(normalise))) changes.evidence = evidence;
  return changes;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <details className="edit-section">
      <summary className="edit-section__summary">
        {title}
        {hint !== undefined && <span className="muted small"> — {hint}</span>}
      </summary>
      <div className="edit-section__body">{children}</div>
    </details>
  );
}

function versionLabel(version: SourceVersion): string {
  const created = version.sourceCreatedAt !== undefined ? ` · created ${formatDate(version.sourceCreatedAt)}` : '';
  // Only the server's top-level decision counts; imported metadata is untrusted.
  const shared = version.memberContentApproved === true ? ' · shared with members' : '';
  const derived = version.textProjection === 'derived' ? ' · derived text, not exact' : '';
  return `Observed ${formatDate(version.observedAt)} · ${version.contentHash.slice(0, 10)}${created}${derived}${shared}`;
}

function VersionPicker({ sourceId, value, index, onChange }: { sourceId: string; value: string; index: number; onChange: (id: string) => void }) {
  const source = useSource(sourceId, sourceId !== '');
  const versions = [...(source.data?.versions ?? [])].sort((a, b) => b.observedAt.localeCompare(a.observedAt));
  let placeholder = 'Not pinned to a version';
  if (sourceId === '') placeholder = 'Choose a source first';
  else if (source.isPending) placeholder = 'Loading versions…';
  else if (source.isError) placeholder = 'Versions not visible to you';
  else if (versions.length === 0) placeholder = 'No stored versions (metadata only)';
  const orphan = value !== '' && !versions.some((version) => version.id === value);
  return (
    <label className="field">
      <span className="field__label">Pinned version</span>
      <select
        className="input"
        aria-label={`Pinned version for source ${String(index)}`}
        value={value}
        disabled={sourceId === '' || (versions.length === 0 && value === '')}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>
        {versions.map((version) => (
          <option key={version.id} value={version.id}>
            {versionLabel(version)}
          </option>
        ))}
        {orphan && <option value={value}>Currently pinned version (not visible to you)</option>}
      </select>
    </label>
  );
}

function EvidenceEditor({ rows, onChange }: { rows: EvidenceDraft[]; onChange: (rows: EvidenceDraft[]) => void }) {
  const update = (key: number, patch: Partial<EvidenceDraft>) => onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  return (
    <div className="evidence-editor">
      {rows.length === 0 && <p className="field__error">No evidence is cited. A finding without evidence cannot be published.</p>}
      <ol className="evidence-editor__rows">
        {rows.map((row, position) => {
          const index = position + 1;
          const classLabel = evidenceClassLabel(row.metadata?.evidenceClass);
          return (
            <li key={row.key} className="evidence-editor__row">
              <SourcePicker
                label={`Source ${String(index)}`}
                value={row.sourceId}
                emptyLabel="No source chosen"
                onChange={(sourceId) => update(row.key, { sourceId, versionId: '' })}
              />
              <div className="evidence-editor__grid">
                <label className="field">
                  <span className="field__label">Relation</span>
                  <select
                    className="input"
                    aria-label={`Relation for source ${String(index)}`}
                    value={row.relation}
                    onChange={(event) => update(row.key, { relation: event.target.value as EvidenceRelation })}
                  >
                    {Object.entries(relationMeta).map(([relation, meta]) => (
                      <option key={relation} value={relation}>
                        {meta.label}
                      </option>
                    ))}
                  </select>
                </label>
                <VersionPicker sourceId={row.sourceId} value={row.versionId} index={index} onChange={(versionId) => update(row.key, { versionId })} />
              </div>
              <label className="field">
                <span className="field__label">Note</span>
                <input
                  className="input"
                  aria-label={`Note for source ${String(index)}`}
                  value={row.note}
                  onChange={(event) => update(row.key, { note: event.target.value })}
                />
              </label>
              {classLabel !== null && <p className="small muted">Classified as {classLabel.toLowerCase()}; the classification is kept.</p>}
              <button type="button" className="button button--link" onClick={() => onChange(rows.filter((item) => item.key !== row.key))}>
                Remove source {index}
              </button>
            </li>
          );
        })}
      </ol>
      <button
        type="button"
        className="button button--secondary"
        onClick={() =>
          onChange([...rows, { key: Math.max(0, ...rows.map((row) => row.key)) + 1, sourceId: '', versionId: '', relation: 'supports', note: '' }])
        }
      >
        Cite another source
      </button>
    </div>
  );
}

/** Depth-on-demand editing of every substantive field of the next version. */
export function ReviewEditor({ draft, onChange }: { draft: FindingDraft; onChange: (draft: FindingDraft) => void }) {
  const report = useReport();
  const projects = useProjects();
  const set =
    <K extends keyof FindingDraft>(key: K) =>
    (value: FindingDraft[K]) =>
      onChange({ ...draft, [key]: value });
  const chapters = report.data?.chapters ?? [];
  const projectItems = projects.data?.items ?? [];

  return (
    <div className="review-editor">
      <Section title="Title, summary and interpretation">
        <Field label="Title">
          {(props) => <input {...props} className="input" value={draft.title} onChange={(event) => set('title')(event.target.value)} />}
        </Field>
        <Field label="Summary">
          {(props) => <textarea {...props} className="input textarea" rows={3} value={draft.summary} onChange={(event) => set('summary')(event.target.value)} />}
        </Field>
        <Field label="Interpretation">
          {(props) => (
            <textarea {...props} className="input textarea" rows={4} value={draft.interpretation} onChange={(event) => set('interpretation')(event.target.value)} />
          )}
        </Field>
      </Section>
      <Section title="Scope, method and cutoff">
        <Field label="Scope">
          {(props) => <textarea {...props} className="input textarea" rows={3} value={draft.scope} onChange={(event) => set('scope')(event.target.value)} />}
        </Field>
        <Field label="Method">
          {(props) => <textarea {...props} className="input textarea" rows={3} value={draft.method} onChange={(event) => set('method')(event.target.value)} />}
        </Field>
        <Field label="Sources considered up to (UTC)">
          {(props) => (
            <input {...props} type="datetime-local" className="input" value={draft.sourceCutoff} onChange={(event) => set('sourceCutoff')(event.target.value)} />
          )}
        </Field>
      </Section>
      <Section title="Uncertainty, implications and counterexamples" hint="one point per line">
        <Field label="Uncertainty">
          {(props) => <textarea {...props} className="input textarea" rows={4} value={draft.uncertainty} onChange={(event) => set('uncertainty')(event.target.value)} />}
        </Field>
        <Field label="Implications">
          {(props) => (
            <textarea {...props} className="input textarea" rows={3} value={draft.implications} onChange={(event) => set('implications')(event.target.value)} />
          )}
        </Field>
        <Field label="Counterexamples">
          {(props) => (
            <textarea {...props} className="input textarea" rows={3} value={draft.counterexamples} onChange={(event) => set('counterexamples')(event.target.value)} />
          )}
        </Field>
      </Section>
      <Section title="Chapter, project and themes">
        <Field label="Chapter">
          {(props) => (
            <select {...props} className="input" value={draft.chapter} onChange={(event) => set('chapter')(event.target.value)}>
              {!chapters.some((chapter) => chapter.id === draft.chapter) && <option value={draft.chapter}>Current chapter</option>}
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.title}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Project">
          {(props) => (
            <select {...props} className="input" value={draft.projectId} onChange={(event) => set('projectId')(event.target.value)}>
              {draft.projectId === '' && <option value="">No project</option>}
              {draft.projectId !== '' && !projectItems.some((project) => project.id === draft.projectId) && (
                <option value={draft.projectId}>Current project</option>
              )}
              {projectItems.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Themes" help="Comma separated.">
          {(props) => <input {...props} className="input" value={draft.themes} onChange={(event) => set('themes')(event.target.value)} />}
        </Field>
      </Section>
      <Section title="Evidence and source versions" hint="pin exact versions once originals are recovered">
        <EvidenceEditor rows={draft.evidence} onChange={set('evidence')} />
      </Section>
    </div>
  );
}
